import {
  Badge,
  Banner,
  Box,
  Button,
  FlexBox,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import classNames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useState } from 'react';

import { BUSINESS_HOURS_MAX_NUMBER_SCHEDULES } from 'app/app.constants';
import { useSchedules } from 'components/settings/components/sections/openingHours/utils/useSchedules';
import { useEditorContext } from 'flow-builder/editor.context';
import type { Relation } from 'flow-builder/types/relation';

import { ScheduleSelect } from '../../../businesshours/components/scheduleSelect/scheduleSelect';
import type { BaseProps } from '../../types/props';
import { createBusinessHoursNode } from '../../utils/createBusinessHoursNode';

import styles from './existingSchedule.module.scss';

type Props = BaseProps & {
  onCreateNewSchedule: VoidFunction;
};

export function ExistingSchedule({
  node,
  replaceNode,
  triggerClose,
  onCreateNewSchedule,
}: Props): JSX.Element {
  const {
    state: { setSelectState },
  } = useEditorContext();
  const [selectedSchedule, setSelectedSchedule] = useState<string | undefined>(
    undefined,
  );

  const { totalNumberOfSchedules } = useSchedules();

  const handleUpdateNode = () => {
    if (!selectedSchedule) {
      throw new Error('No schedule selected');
    }

    ampli.businessHoursMigrationWizardOpeningHoursFormSubmitted();

    const newNode = createBusinessHoursNode(node, selectedSchedule);

    replaceNode(newNode);
    triggerClose();
    setSelectState({ node: newNode as Relation });
  };

  const handleCancelModal = useCallback(() => {
    ampli.businessHoursMigrationWizardOpeningHoursFormCancelled();
    triggerClose();
  }, [triggerClose]);

  const isCreateDisabled = !!selectedSchedule;
  const hasReachedMaxNumberSchedules =
    totalNumberOfSchedules >= BUSINESS_HOURS_MAX_NUMBER_SCHEDULES;

  return (
    <>
      <ModalHead
        title={
          <>
            Opening hours
            <Badge size="small" variant="info" className={styles.badge}>
              Update available
            </Badge>
          </>
        }
        onClose={handleCancelModal}
      />
      <ModalBody>
        {hasReachedMaxNumberSchedules && (
          <Banner
            className={styles.banner}
            variant="info"
            description={
              <Text.Paragraph color={colors.neutral[800]} variant="4">
                You can only replace your opening hours with one of your
                existing schedules, since you have reached the maximum number of
                schedules.
              </Text.Paragraph>
            }
          />
        )}
        <FlexBox
          background="neutral.100"
          borderRadius="100"
          padding="300"
          flexDirection="column"
          alignItems="center"
          margin="0 0 300"
        >
          <Text.Paragraph variant="1">Replace with a schedule</Text.Paragraph>
          <Box width="100%">
            <ScheduleSelect
              label={null}
              onChange={setSelectedSchedule}
              value={selectedSchedule || ''}
            />
          </Box>
        </FlexBox>
        {!hasReachedMaxNumberSchedules && (
          <Box
            className={classNames({
              [styles.newScheduleWrapperIsDisabled]: isCreateDisabled,
            })}
          >
            <Text.Paragraph align="center" subdued variant="1">
              or
            </Text.Paragraph>
            <FlexBox
              background="neutral.100"
              borderRadius="100"
              padding="300"
              flexDirection="column"
              alignItems="center"
              gap="300"
              margin="300 0 0"
            >
              <Text.Paragraph variant="1">
                Turn your opening hours into a schedule
              </Text.Paragraph>
              <Text.Paragraph variant="2">
                Create a new schedule based on your opening hours in 4 steps.
              </Text.Paragraph>
              <Button
                variant="secondary"
                onClick={() => {
                  ampli.businessHoursMigrationWizardCreateNewScheduleButtonClicked();
                  onCreateNewSchedule();
                }}
              >
                Create new schedule
              </Button>
            </FlexBox>
          </Box>
        )}
      </ModalBody>
      <ModalFoot
        cancelOnClick={handleCancelModal}
        confirmName="Update"
        confirmProps={{ disabled: !isCreateDisabled }}
        confirmOnClick={handleUpdateNode}
      />
    </>
  );
}
